import config from "config";
import { LDContext, LDProvider } from "launchdarkly-react-client-sdk";
import React, { ReactNode, useEffect, useState } from "react";

type LaunchDarklyProviderProps = {
  children: ReactNode;
  /**
   * Explicit LD context key. Authoritative when set: it takes precedence over
   * the sessionStorage cache and the auth lookup, and is not itself cached.
   * The get-started page passes the key the backend attributes its conversion
   * events to, so the events the browser emits attribute to the same visitor.
   */
  contextKey?: string | null;
  /** Only meaningful alongside `contextKey`, which carries no login state. */
  isLoggedIn?: boolean;
  userId?: string | null;
};

interface AuthResponse {
  email?: string;
  userId?: string;
}

const getUnivCookie = (): string => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; minted_univ_cookie_id=`);
  return parts.length === 2 ? (parts.pop()!.split(";").shift() ?? "") : "";
};

const getLaunchDarklyContext = (
  userIdentifier: string | null | undefined,
  isLoggedIn = false,
  isEmployee = false,
): LDContext => ({
  isEmployee,
  isLoggedIn,
  key: userIdentifier || "anonymous-user",
  kind: "user",
});

const useLaunchDarkly = Boolean(config.get("launchDarklyClientSideId"));

// Define specific flags that the application uses to optimize flag evaluation
export const FRONTEND_FLAGS = {
  autoParseContactName: "auto-parse-guests", // Experiment
  guestEmailsEnabled: "guest-emails-enabled", // Boolean
  weddingClickToEdit: "wedding-click-to-edit", // Boolean
};

export const TRACKING = {
  ButtonAddElementClicked: "buttonAddElementClicked",
  CashRegistryCreated: "cashRegistryCreated",
  DrawerEditSaved: "gmDrawerEditSaved",
  GuestAdded: "guestAdded",
  GuestAddedBulk: "guestAddedBulk",
  GuestsAddedCount: "guestsAddedCount",
  ReturnToAddSession: "returnToAddSession",
  InlineEditSaved: "gmInlineEditSaved",
  MobileNavOpened: "mobileNavOpened",
  NavItemClicked: "navItemClicked",
  NavSubItemClicked: "navSubItemClicked",
  PreviewMyWebsiteClicked: "previewMyWebsiteClicked",
  PreviewSectionAddElementClicked: "previewSectionAddElementClicked",
  PreviewSectionEditClicked: "previewSectionEditClicked",
  ReturnDashboardVisit: "returnDashboardVisit",
  ReturnDashboardVisitWithin14Days: "returnDashboardVisitWithin14Days",
  SectionCreated: "sectionCreated",
  SectionUpdated: "sectionUpdated",
  SidebarSectionEditClicked: "sidebarSectionEditClicked",
  SiteHasReceivedRsvps: "siteHasReceivedRsvps",
  ViewWebsiteClicked: "viewWebsiteClicked",
  faqCreated: "faqCreated",
};

/**
 * Provides LaunchDarkly flags to the tree. `deferInitialization` makes the
 * client wait on the async context, not the tree wait on the client.
 */
export const LaunchDarklyProvider = ({
  children,
  contextKey = null,
  isLoggedIn = false,
  userId = null,
}: LaunchDarklyProviderProps) => {
  const [ldContext, setLdContext] = useState<LDContext | undefined>(undefined);

  // Fetch the user ID if not already set, then initialize launchdarkly
  useEffect(() => {
    if (!useLaunchDarkly) {
      return;
    }

    // Deliberately not cached: the caller owns this key, and writing it would
    // leak a page-specific context into the rest of the session.
    if (contextKey) {
      setLdContext(getLaunchDarklyContext(contextKey, isLoggedIn));

      return;
    }

    const cachedContext = window.sessionStorage.getItem("launchDarklyContext");

    if (cachedContext) {
      setLdContext(JSON.parse(cachedContext) as LDContext);
    } else if (userId) {
      const newContext = getLaunchDarklyContext(`${userId}`, true);

      window.sessionStorage.setItem(
        "launchDarklyContext",
        JSON.stringify(newContext),
      );
      setLdContext(newContext);
    } else {
      fetch(`${config.get("mintedAuthUrl")}`, {
        credentials: "include",
      })
        .then((response) => {
          if (!response.ok) {
            throw new Error(
              `Auth request failed: ${response.status} ${response.statusText}`,
            );
          }

          return response.json() as Promise<AuthResponse>;
        })
        .then((auth) => {
          let newContext: LDContext;

          if (auth.userId) {
            const userIdentifier = `${auth.userId}`;
            const employee =
              auth.email?.toLowerCase().endsWith("@minted.com") ?? false;

            newContext = getLaunchDarklyContext(userIdentifier, true, employee);
            window.sessionStorage.setItem(
              "launchDarklyContext",
              JSON.stringify(newContext),
            );
          } else {
            newContext = getLaunchDarklyContext(getUnivCookie());
          }

          setLdContext(newContext);
        })
        .catch((error) => {
          console.error("Error fetching user ID:", error);
          setLdContext(getLaunchDarklyContext(null));
        });
    }
  }, [contextKey, isLoggedIn, userId]);

  if (!useLaunchDarkly) {
    return <>{children}</>;
  }

  // Keep this element type stable: swapping it once the context resolves would
  // remount the whole subtree and discard every child's state.
  return (
    <LDProvider
      clientSideID={config.get("launchDarklyClientSideId") as string}
      context={ldContext}
      deferInitialization
    >
      {children}
    </LDProvider>
  );
};
